import { useMemo } from 'react';
import moment from 'moment';

import { useGroupLessons, IGroupLessonRow } from './useGroupTabsData';

/**
 * useGroupRecentActivity — a thin, side-effect-light selector that composes a
 * small "recent activity" feed for the Overview tab (Req 2.3) WITHOUT
 * introducing a new network query.
 *
 * Design note (honest limitation): the only group-scoped timeline signal that
 * is reliably already in the Apollo cache when the Overview tab is open is the
 * lesson schedule. The Overview tab's `UpcomingLessonsSection` already reads
 * `useGroupLessons(groupId)`; this hook re-reads the SAME query (Apollo dedupes
 * by operation + variables, so no extra round-trip is issued) and projects the
 * already-passed lessons into recent-activity items. Materials/sync timestamps
 * would each require triggering their own queries here — which would violate
 * the "no new query" constraint — so they are intentionally NOT pulled. If a
 * cheaper always-cached signal becomes available later, it can be folded into
 * the same projection.
 *
 * The hook degrades gracefully: when no lessons are available (loading, error,
 * or genuinely empty), `items` is `[]`, and `RecentActivitySection` shows its
 * localized "no recent activity" message.
 */

const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMATS = ['HH:mm:ss', 'HH:mm'];

/** How many recent items to surface (most-recent first). */
const RECENT_ACTIVITY_LIMIT = 5;

/** The kind of change a recent-activity item represents. */
export type GroupRecentActivityKind = 'lessonPassed';

/**
 * One recent-activity item. Intentionally i18n-free: the component localizes
 * using `kind` (e.g. `pages.groupDetail.overview.recentActivity.lessonPassed`)
 * and renders `title` (a plain subject name) verbatim.
 */
export interface IGroupRecentActivityItem {
  /** Stable identity for React keys (the underlying lesson id). */
  id: string;
  /** Discriminator the component maps to a localized label. */
  kind: GroupRecentActivityKind;
  /** Plain-text subject of the change (the lesson name). */
  title: string;
  /** Epoch millis the change occurred at; the feed is sorted descending. */
  timestamp: number;
}

interface IUseGroupRecentActivity {
  items: IGroupRecentActivityItem[];
  loading: boolean;
}

/** Resolve the scheduled instant (epoch millis) of a lesson, or `null`. */
const toScheduledMillis = (lesson: IGroupLessonRow): number | null => {
  if (!lesson.date) {
    return null;
  }

  const day = moment(lesson.date, DATE_FORMAT, true);
  if (!day.isValid()) {
    return null;
  }

  const rawTime = lesson.startTime?.trim();
  if (rawTime) {
    const time = moment(rawTime, TIME_FORMATS, true);
    if (time.isValid()) {
      day.hour(time.hour()).minute(time.minute()).second(time.second());
    }
  }

  return day.valueOf();
};

/**
 * Compose the recent-activity feed for a group from its already-cached lesson
 * schedule. Returns the most recently passed lessons (scheduled at/before now)
 * in reverse-chronological order, capped at `RECENT_ACTIVITY_LIMIT`.
 */
export const useGroupRecentActivity = (
  groupId?: string,
): IUseGroupRecentActivity => {
  const { group, loading } = useGroupLessons(groupId);

  const items = useMemo<IGroupRecentActivityItem[]>(() => {
    const lessons = group?.lessons;
    if (!lessons || lessons.length === 0) {
      return [];
    }

    const now = Date.now();

    return lessons
      .map((lesson) => ({ lesson, scheduled: toScheduledMillis(lesson) }))
      .filter(
        (entry): entry is { lesson: IGroupLessonRow; scheduled: number } =>
          entry.scheduled !== null && entry.scheduled <= now,
      )
      .sort((a, b) => b.scheduled - a.scheduled)
      .slice(0, RECENT_ACTIVITY_LIMIT)
      .map(
        ({ lesson, scheduled }): IGroupRecentActivityItem => ({
          id: lesson.id,
          kind: 'lessonPassed',
          title: lesson.name || '',
          timestamp: scheduled,
        }),
      );
  }, [group?.lessons]);

  return { items, loading };
};
